import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchOpenApiDocument,
  groupByTag,
  operationsOf,
  OPENAPI_JSON_PATH,
  OPENAPI_YAML_PATH,
  type ApiOperation,
  type OpenApiSchema,
} from '../api/openapi.ts';
import { useSession } from '../state/session.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { Alert, Button, EmptyState, LoadingBlock, PageHeader, TextInput, classes } from '../components/ui.tsx';
import './api-docs.css';

/**
 * The API reference (Settings → API reference), rendered from the deployment's
 * own OpenAPI document at request time.
 *
 * Generated rather than written, for the same reason the document itself is:
 * a hand-kept page is a second statement of what the API does, and the day it
 * disagrees with the first is the day somebody follows it into a 400. Every
 * endpoint, parameter, body field and scope on this screen came out of
 * `/api/v1/openapi.json` moments ago, so an endpoint that is not reachable
 * cannot appear here and a body whose schema changed changes here with it.
 *
 * Two things are deliberately different from a Swagger UI:
 *
 *   * **It sends GET requests only.** The console runs against the signed-in
 *     session and this company's real data, so a "try it" that could POST
 *     would put emailing a customer, refunding a card and deleting a record
 *     one stray click away inside the documentation. Writes are shown as a
 *     curl to copy, which makes sending one a decision taken in a terminal.
 *   * **The API's own prose stays in English.** Summaries and parameter
 *     descriptions are quoted from the document — they are what a developer
 *     will read in a generated client — while everything this page says for
 *     itself is translated. A path, a method and a scope read the same in
 *     both languages.
 */

const METHOD_TONE: Record<string, string> = {
  get: 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  post: 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  patch: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
  delete: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
};

function MethodBadge({ method }: { method: string }) {
  return (
    <span
      className={classes('badge w-fit font-mono uppercase', METHOD_TONE[method] ?? 'border-[color:var(--color-line)]')}
    >
      {method}
    </span>
  );
}

/** `{"type":"string","format":"email"}` → `string · email`, for one line. */
function describeType(schema: OpenApiSchema | undefined): string {
  if (!schema) return '';
  const alternatives = schema.anyOf ?? schema.oneOf;
  if (alternatives) {
    const parts = alternatives.map((option) => describeType(option)).filter(Boolean);
    return [...new Set(parts)].join(' | ');
  }
  const bits = [schema.type ?? 'any'];
  if (schema.format) bits.push(schema.format);
  if (schema.enum) bits.push(schema.enum.map((value) => JSON.stringify(value)).join(' | '));
  return bits.join(' · ');
}

/**
 * A request body's fields as a list, one level deep.
 *
 * One level on purpose: these schemas come from zod, and the nested ones are
 * arrays of line items whose shape is better read in the document itself than
 * in an indented tree that pushes the endpoint's own fields off the screen.
 * The full schema is always a click away in `openapi.json`.
 */
function BodyFields({ schema }: { schema: OpenApiSchema }) {
  const { t } = useTranslation();
  const properties = Object.entries(schema.properties ?? {});
  if (properties.length === 0) return <p className="text-sm text-[color:var(--color-muted)]">{t('apiDocs.anyJson')}</p>;

  const required = new Set(schema.required ?? []);
  return (
    <ul className="space-y-1.5">
      {properties.map(([name, property]) => (
        <li key={name} className="text-sm">
          <span className="font-mono text-xs">{name}</span>
          <span className="ml-2 text-xs text-[color:var(--color-muted)]">{describeType(property)}</span>
          {required.has(name) ? (
            <span className="ml-2 text-xs text-[color:var(--color-danger)]">{t('apiDocs.required')}</span>
          ) : null}
          {property.description ? (
            <span className="ml-2 text-xs text-[color:var(--color-muted)]">{property.description}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * A placeholder for one field, chosen from what the schema says about it.
 *
 * The point is a body somebody edits rather than one they rebuild: an empty
 * `{}` posted at an endpoint that requires `customerId` is a 400 dressed up as
 * an example, and the developer has to go back to the field list to find out
 * what was missing. An enum offers its first value, an id-shaped name offers an
 * id-shaped string, and everything else offers its own type.
 */
function placeholderFor(name: string, schema: OpenApiSchema | undefined, depth = 0): unknown {
  const resolved = schema?.anyOf?.[0] ?? schema?.oneOf?.[0] ?? schema;
  if (resolved?.enum?.length) return resolved.enum[0];
  if (resolved?.default !== undefined) return resolved.default;
  switch (resolved?.type) {
    case 'integer':
    case 'number':
      return resolved.minimum ?? 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'object':
      // Down into it, because a required object usually has required fields of
      // its own: `POST /quotes/:id/rooms` takes a `room`, and a `room` with no
      // `roomName` fails validation exactly as an absent one would. The depth
      // stop is for a schema that refers to itself — three levels is deeper
      // than any body here, and a skeleton is a starting point, not the
      // document.
      return depth >= 3 ? {} : requiredSkeleton(resolved, depth + 1);
    default:
      if (resolved?.format === 'email') return 'name@example.com';
      if (resolved?.format === 'date') return '2026-01-31';
      if (/Id$/.test(name)) return '01ARZ3NDEKTSV4RRFFQ69G5FAV';
      return 'string';
  }
}

/**
 * The JSON body to offer, built from the schema's REQUIRED fields.
 *
 * Required only: a skeleton carrying every optional field is a wall of nulls
 * that hides the three lines that matter, and the whole field list is on the
 * screen directly above this. Where nothing is required, `{}` is honest — the
 * endpoint takes a body but insists on nothing in it.
 */
function requiredSkeleton(schema: OpenApiSchema | null | undefined, depth = 0): Record<string, unknown> {
  const properties = schema?.properties ?? {};
  const skeleton: Record<string, unknown> = {};
  for (const name of schema?.required ?? []) skeleton[name] = placeholderFor(name, properties[name], depth);
  return skeleton;
}

/** How many fields to offer where the schema names no individual one. */
const SUGGESTED_FIELDS = 3;

/**
 * The JSON body to offer.
 *
 * Required fields, where there are any. Where there are none but the body is
 * still mandatory, the endpoint is one whose rule spans fields rather than
 * naming one — `POST /customers` needs a first name, a last name or a company
 * name, and no single one of them is required. An empty object is the one body
 * guaranteed to fail there, so the first few fields are offered instead: a
 * starting point to edit, with the full list on the screen above it.
 */
function bodySkeleton(schema: OpenApiSchema | null, required: boolean, example?: unknown): string {
  // The document's own example first, where there is one: it was checked
  // against the schema when the document was built, so it satisfies the rules
  // no JSON Schema can express.
  if (example !== undefined && example !== null) return JSON.stringify(example, null, 2);

  const skeleton = requiredSkeleton(schema);
  if (Object.keys(skeleton).length === 0 && required) {
    for (const [name, property] of Object.entries(schema?.properties ?? {}).slice(0, SUGGESTED_FIELDS)) {
      skeleton[name] = placeholderFor(name, property);
    }
  }
  return JSON.stringify(skeleton, null, 2);
}

/**
 * The multipart parts, as `-F` flags.
 *
 * Every part the document describes, not just the file: `POST /files/quotes/…`
 * reads a `category`, and `quote_photo` is what puts an image on the quote
 * rather than filing it as an attachment. A command carrying only the file can
 * do exactly one of the two things this endpoint does.
 */
function uploadFlags(schema: OpenApiSchema | null): string {
  const parts = Object.entries(schema?.properties ?? {});
  if (parts.length === 0) return ` \\\n  -F file=@./photo.jpg`;
  return parts
    .map(([name, property]) =>
      property.format === 'binary'
        ? ` \\\n  -F ${name}=@./photo.jpg`
        : ` \\\n  -F ${name}=${String(placeholderFor(name, property))}`,
    )
    .join('');
}

/** The curl a developer would run, with whatever they have filled in so far. */
function curlFor(operation: ApiOperation, values: Record<string, string>, origin: string): string {
  const url = `${origin}${resolvePath(operation, values)}${queryFor(operation, values)}`;
  const method = operation.method === 'get' ? '' : ` -X ${operation.method.toUpperCase()}`;
  // No `-d` where the endpoint reads no body: an action whose whole input is
  // its path should not be shown being posted an empty object.
  const body =
    operation.requestContentType === 'application/json' && operation.method !== 'get'
      ? ` \\\n  -H 'content-type: application/json' \\\n  -d '${bodySkeleton(operation.requestSchema, operation.requestRequired, operation.requestExample)}'`
      : '';
  const upload = operation.requestContentType === 'multipart/form-data' ? uploadFlags(operation.requestSchema) : '';
  return `curl${method} -H "Authorization: Bearer $BLASTO_API_KEY"${body}${upload} \\\n  '${url}'`;
}

/** `/api/v1/quotes/{quoteId}` + `{quoteId: '01…'}` → `/api/v1/quotes/01…`. */
function resolvePath(operation: ApiOperation, values: Record<string, string>): string {
  return operation.path.replace(/\{([^}]+)\}/g, (whole, name: string) => values[`path:${name}`] || whole);
}

function queryFor(operation: ApiOperation, values: Record<string, string>): string {
  const params = new URLSearchParams();
  for (const parameter of operation.parameters) {
    if (parameter.in !== 'query') continue;
    const value = values[`query:${parameter.name}`];
    if (value) params.set(parameter.name, value);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

interface ConsoleResult {
  status: number;
  durationMs: number;
  body: string;
  requestId: string | null;
  url: string;
}

function OperationCard({ operation }: { operation: ApiOperation }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ConsoleResult | null>(null);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const origin = window.location.origin;
  const pathParams = operation.parameters.filter((parameter) => parameter.in === 'path');
  const queryParams = operation.parameters.filter((parameter) => parameter.in === 'query');
  // Path parameters, and the query parameters the endpoint refuses to work
  // without — `/schedule/appointments` needs its date range, and sending
  // without one is a 400 the page could have predicted.
  const missing = [...pathParams, ...queryParams].filter(
    (parameter) => parameter.required && !values[`${parameter.in}:${parameter.name}`],
  );
  const editable = [...pathParams, ...queryParams];
  // Only GET runs from this page — see the note at the top of the file.
  const runnable = operation.method === 'get';

  const set = (key: string, value: string) => setValues((current) => ({ ...current, [key]: value }));

  async function send() {
    setSending(true);
    setFailure(null);
    // The previous answer goes with it. Left on screen, a failed retry would
    // show an error alert above a body from a request with different
    // parameters — which reads as the failure's own response.
    setResult(null);
    const url = `${resolvePath(operation, values)}${queryFor(operation, values)}`;
    const started = performance.now();
    try {
      const response = await fetch(url, { headers: { accept: 'application/json' }, credentials: 'same-origin' });
      const type = response.headers.get('content-type') ?? '';
      // A PDF or a photo is not something to print into the page; the status
      // line is the useful part, and the browser can fetch the file itself.
      const text = type.includes('application/json')
        ? JSON.stringify(await response.json(), null, 2)
        : t('apiDocs.binaryBody', { type: type.split(';')[0] || 'unknown' });
      setResult({
        status: response.status,
        durationMs: Math.round(performance.now() - started),
        body: text.length > 20_000 ? `${text.slice(0, 20_000)}\n…` : text,
        requestId: response.headers.get('x-request-id'),
        url,
      });
    } catch (caught) {
      setFailure(caught instanceof Error ? caught.message : t('apiDocs.sendFailed'));
    } finally {
      setSending(false);
    }
  }

  async function copyCurl() {
    // A restricted browser or an embedded webview refuses this outright. The
    // command is on the screen either way, so the honest answer is to say the
    // copy did not happen rather than flash "Copied" over a clipboard that
    // still holds something else.
    try {
      await navigator.clipboard.writeText(curlFor(operation, values, origin));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyFailed(true);
    }
  }

  return (
    <li className="border-t border-[color:var(--color-line)] first:border-t-0">
      {/* On a phone the method badge sits above the path as a column, so the
          row reads as two stacked parts rather than a label that wrapped. */}
      <button
        type="button"
        className="flex w-full min-w-0 flex-col items-start gap-3 px-1 py-2.5 text-left sm:grid sm:grid-cols-[auto_minmax(0,1fr)_auto]"
        aria-expanded={open}
        // The visible row is three adjacent elements with no whitespace
        // between them, which a screen reader would read as one run-on word.
        // Method, path and scope are the same in every language, so the label
        // is built rather than translated.
        aria-label={`${operation.method.toUpperCase()} ${operation.path} · ${operation.scope ?? t('apiDocs.noScope')}`}
        onClick={() => setOpen((current) => !current)}
      >
        <MethodBadge method={operation.method} />
        <code className="api-operation-path min-w-0 font-mono text-xs">{operation.path}</code>
        <span className="min-w-0 hidden text-xs text-[color:var(--color-muted)] sm:inline">
          {operation.scope ?? t('apiDocs.noScope')}
        </span>
      </button>

      {open ? (
        <div className="space-y-4 px-1 pb-4">
          {operation.summary ? <p className="text-sm">{operation.summary}</p> : null}

          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[color:var(--color-muted)]">
            <div>
              <dt className="inline font-medium">{t('apiDocs.scope')}: </dt>
              <dd className="inline font-mono">{operation.scope ?? t('apiDocs.noScope')}</dd>
            </div>
            <div>
              <dt className="inline font-medium">{t('apiDocs.success')}: </dt>
              <dd className="inline font-mono">{operation.successStatus}</dd>
            </div>
            {operation.id ? (
              <div>
                <dt className="inline font-medium">{t('apiDocs.operationId')}: </dt>
                <dd className="inline font-mono">{operation.id}</dd>
              </div>
            ) : null}
          </dl>

          {operation.parameters.length > 0 ? (
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide">{t('apiDocs.parameters')}</h4>
              <ul className="space-y-1.5">
                {operation.parameters.map((parameter) => (
                  <li key={`${parameter.in}:${parameter.name}`} className="text-sm">
                    <span className="font-mono text-xs">{parameter.name}</span>
                    <span className="ml-2 text-xs text-[color:var(--color-muted)]">
                      {parameter.in} · {describeType(parameter.schema)}
                    </span>
                    {parameter.required ? (
                      <span className="ml-2 text-xs text-[color:var(--color-danger)]">{t('apiDocs.required')}</span>
                    ) : null}
                    {parameter.description ? (
                      <span className="ml-2 text-xs text-[color:var(--color-muted)]">{parameter.description}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {operation.requestSchema ? (
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide">
                {t('apiDocs.requestBody')}
                <span className="ml-2 font-mono text-[10px] font-normal normal-case text-[color:var(--color-muted)]">
                  {operation.requestContentType}
                </span>
              </h4>
              <BodyFields schema={operation.requestSchema} />
            </div>
          ) : null}

          {/* ---- The console ------------------------------------------------ */}
          <div className="rounded-md border border-[color:var(--color-line)] p-3">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide">
              {runnable ? t('apiDocs.tryIt') : t('apiDocs.buildRequest')}
            </h4>

            {/*
              The inputs are here for a write too, even though the button is
              not. They fill in the curl below, and without them a copied
              command for `POST /quotes/{quoteId}/send` still carries the
              literal `{quoteId}` — a command that cannot address anything,
              offered as if it were ready to run.
            */}
            {editable.length > 0 ? (
              <div className="mb-3 grid gap-2 sm:grid-cols-2">
                {editable.map((parameter) => (
                  <TextInput
                    key={`${parameter.in}:${parameter.name}`}
                    label={parameter.name}
                    required={parameter.required}
                    value={values[`${parameter.in}:${parameter.name}`] ?? ''}
                    onChange={(event) => set(`${parameter.in}:${parameter.name}`, event.target.value)}
                    placeholder={String(parameter.schema?.default ?? '')}
                  />
                ))}
              </div>
            ) : null}

            {runnable ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="primary" busy={sending} disabled={missing.length > 0} onClick={() => void send()}>
                  {t('apiDocs.send')}
                </Button>
                <span className="text-xs text-[color:var(--color-muted)]">
                  {missing.length > 0
                    ? t('apiDocs.fillPathParams', { names: missing.map((one) => one.name).join(', ') })
                    : t('apiDocs.sendsAsYou')}
                </span>
              </div>
            ) : (
              <p className="text-xs text-[color:var(--color-muted)]">{t('apiDocs.writesNotSent')}</p>
            )}

            {failure ? (
              <div className="mt-3">
                <Alert tone="error">{failure}</Alert>
              </div>
            ) : null}

            {result ? (
              <div className="mt-3">
                <p className="mb-1 font-mono text-xs">
                  <span
                    className={
                      result.status < 300
                        ? 'text-[color:var(--color-brand-strong)]'
                        : 'text-[color:var(--color-danger)]'
                    }
                  >
                    {result.status}
                  </span>
                  <span className="ml-2 text-[color:var(--color-muted)]">
                    {result.url} · {result.durationMs} ms
                    {result.requestId ? ` · ${result.requestId}` : ''}
                  </span>
                </p>
                <pre className="max-h-80 overflow-auto rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3 text-xs leading-relaxed">
                  {result.body}
                </pre>
              </div>
            ) : null}
          </div>

          {/* ---- curl -------------------------------------------------------- */}
          <div>
            <div className="mb-1.5 flex flex-wrap items-center gap-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide">{t('apiDocs.curl')}</h4>
              <Button onClick={() => void copyCurl()}>{copied ? t('common.copied') : t('apiDocs.copyCurl')}</Button>
              {copyFailed ? (
                <span className="text-xs text-[color:var(--color-danger)]">{t('apiDocs.copyFailed')}</span>
              ) : null}
            </div>
            <pre className="overflow-x-auto rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3 text-xs leading-relaxed">
              {curlFor(operation, values, origin)}
            </pre>
          </div>
        </div>
      ) : null}
    </li>
  );
}

export default function ApiDocsPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [filter, setFilter] = useState('');
  useDocumentTitle(t('apiDocs.documentTitle'));

  const allowed = can('api_keys:manage');
  const document = useQuery({
    queryKey: ['openapi'],
    queryFn: fetchOpenApiDocument,
    // The document is public, but this page is for the people who can mint a
    // credential to use it with — so it is not fetched for anybody else.
    enabled: allowed,
    staleTime: 5 * 60 * 1000,
  });

  const groups = useMemo(() => {
    if (!document.data) return [];
    const needle = filter.trim().toLowerCase();
    const operations = operationsOf(document.data).filter((operation) => {
      if (!needle) return true;
      return [operation.path, operation.summary, operation.scope ?? '', operation.tag, operation.method].some(
        (haystack) => haystack.toLowerCase().includes(needle),
      );
    });
    return groupByTag(operations);
  }, [document.data, filter]);

  const total = groups.reduce((count, group) => count + group.operations.length, 0);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t('apiDocs.title')} />
        <EmptyState title={t('apiDocs.noAccess')}>{t('apiDocs.noAccessHint')}</EmptyState>
      </>
    );
  }

  return (
    <div className="api-reference">
      <PageHeader title={t('apiDocs.title')} description={t('apiDocs.description')} />

      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        <Link className="font-semibold text-[color:var(--color-brand)] hover:underline" to="/settings">
          {t('apiDocs.backToSettings')}
        </Link>
        <span className="mx-2">·</span>
        <a
          className="font-mono text-[color:var(--color-brand)] hover:underline"
          href={OPENAPI_JSON_PATH}
          target="_blank"
          rel="noreferrer"
        >
          openapi.json
        </a>
        <span className="mx-2">·</span>
        <a
          className="font-mono text-[color:var(--color-brand)] hover:underline"
          href={OPENAPI_YAML_PATH}
          target="_blank"
          rel="noreferrer"
        >
          openapi.yaml
        </a>
      </p>

      {document.isLoading ? <LoadingBlock label={t('apiDocs.loading')} /> : null}

      {document.isError ? <Alert tone="error">{t('apiDocs.loadFailed')}</Alert> : null}

      {document.data ? (
        <>
          <div className="card mb-4">
            <TextInput
              label={t('apiDocs.filterLabel')}
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder={t('apiDocs.filterPlaceholder')}
            />
            <p className="mt-2 text-xs text-[color:var(--color-muted)]">{t('apiDocs.matches', { count: total })}</p>
          </div>

          {total === 0 ? (
            <EmptyState title={t('apiDocs.noMatches')}>{t('apiDocs.noMatchesHint')}</EmptyState>
          ) : (
            groups.map((group) => (
              <section key={group.tag} className="card mb-4" aria-labelledby={`api-tag-${group.tag}`}>
                <h2 id={`api-tag-${group.tag}`} className="mb-2 text-lg font-semibold">
                  {group.tag}
                  <span className="ml-2 text-sm font-normal text-[color:var(--color-muted)]">
                    {group.operations.length}
                  </span>
                </h2>
                <ul>
                  {group.operations.map((operation) => (
                    <OperationCard key={`${operation.method} ${operation.path}`} operation={operation} />
                  ))}
                </ul>
              </section>
            ))
          )}
        </>
      ) : null}
    </div>
  );
}
